// src/components/VisitChecklistModal.jsx
// Modal de checklist de visite avec auto-save debounced (1s).
// Templates différents pour RP et Invest, items custom ajoutables, notes par item.

import React, { useState, useEffect, useMemo, useRef } from "react";
import { getDefaultChecklist, getSectionOrder } from "../utils/visitChecklistTemplates";

export function VisitChecklistModal({ maison, onSave, onClose }) {
  // Initialisation : on prend la checklist persistée, sinon on charge le template par défaut.
  const initial = useMemo(() => {
    if (Array.isArray(maison.visitChecklist) && maison.visitChecklist.length > 0) {
      return maison.visitChecklist;
    }
    return getDefaultChecklist(maison.usage || "residence");
  }, [maison.id]); // re-init uniquement si on change de bien

  const [checklist, setChecklist] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Auto-save debounced 1s
  const saveTimerRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    setDirty(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSave?.({ ...maison, visitChecklist: checklist });
      setDirty(false);
      setLastSaved(new Date());
    }, 1000);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [checklist]);

  // Save immédiat à la fermeture
  const handleClose = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      if (dirty) onSave?.({ ...maison, visitChecklist: checklist });
    }
    onClose?.();
  };

  const updateItem = (index, patch) => {
    setChecklist((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const addCustomItem = (section) => {
    const newItem = {
      id: `custom-${Date.now()}`,
      section,
      label: "",
      checked: false,
      note: "",
      custom: true,
    };
    setChecklist((prev) => [...prev, newItem]);
  };

  const removeItem = (index) => {
    setChecklist((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    if (window.confirm("Réinitialiser la checklist ? Tous les items cochés et notes seront perdus.")) {
      setChecklist(getDefaultChecklist(maison.usage || "residence"));
    }
  };

  // Grouper par section, garder l'ordre canonique du template
  const grouped = useMemo(() => {
    const groups = {};
    checklist.forEach((item, idx) => {
      if (!groups[item.section]) groups[item.section] = [];
      groups[item.section].push({ ...item, _index: idx });
    });
    // Tri des sections : ordre canonique d'abord, puis sections custom à la fin
    const canonical = getSectionOrder(maison.usage || "residence");
    const ordered = {};
    canonical.forEach((s) => {
      if (groups[s]) ordered[s] = groups[s];
    });
    Object.keys(groups).forEach((s) => {
      if (!ordered[s]) ordered[s] = groups[s];
    });
    return ordered;
  }, [checklist, maison.usage]);

  const total = checklist.length;
  const done = checklist.filter((i) => i.checked).length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div onClick={handleClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={modal}>
        {/* Header */}
        <div style={header}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#f1f5f9" }}>📋 Checklist de visite</h2>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {maison.titre || `${maison.type || ""} · ${maison.ville || ""}`}
            </div>
          </div>
          <button onClick={handleClose} style={closeBtn} aria-label="Fermer">✕</button>
        </div>

        {/* Barre de progression */}
        <div style={progressContainer}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Avancement</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#a78bfa" }}>
              {done}/{total} ({percent}%)
            </span>
          </div>
          <div style={progressBg}>
            <div style={{ ...progressFg, width: `${percent}%` }} />
          </div>
        </div>

        {/* Sections */}
        <div style={{ marginTop: 18 }}>
          {Object.entries(grouped).map(([section, items]) => (
            <SectionBlock
              key={section}
              section={section}
              items={items}
              onToggle={(idx) => updateItem(idx, { checked: !checklist[idx].checked })}
              onNote={(idx, note) => updateItem(idx, { note })}
              onLabel={(idx, label) => updateItem(idx, { label })}
              onRemove={removeItem}
              onAddItem={() => addCustomItem(section)}
            />
          ))}
        </div>

        {/* Footer */}
        <div style={footer}>
          <button onClick={handleReset} style={btnDanger}>↺ Reset au template</button>
          <button onClick={handleClose} style={btnPrimary}>Fermer</button>
        </div>

        <div style={{ marginTop: 8, fontSize: 11, color: dirty ? "#f59e0b" : "#6b7280", textAlign: "center" }}>
          {dirty
            ? "⏳ Sauvegarde en cours…"
            : lastSaved
            ? `✓ Sauvegardé à ${lastSaved.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
            : "Auto-save activé (1s après chaque modif)"}
        </div>
      </div>
    </div>
  );
}

// --- Section repliable ---

function SectionBlock({ section, items, onToggle, onNote, onLabel, onRemove, onAddItem }) {
  const [expanded, setExpanded] = useState(true);
  const sectionDone = items.filter((i) => i.checked).length;
  const sectionTotal = items.length;

  return (
    <div style={sectionStyle}>
      <button onClick={() => setExpanded((e) => !e)} style={sectionHeaderBtn}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: "#9ca3af" }}>{expanded ? "▾" : "▸"}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#c4b5fd", textTransform: "uppercase", letterSpacing: "0.04em" }}>{section}</span>
        </div>
        <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>
          {sectionDone}/{sectionTotal}
        </span>
      </button>

      {expanded && (
        <div style={{ paddingTop: 4 }}>
          {items.map((item) => (
            <ChecklistItem
              key={item.id}
              item={item}
              onToggle={() => onToggle(item._index)}
              onNote={(note) => onNote(item._index, note)}
              onLabel={(label) => onLabel(item._index, label)}
              onRemove={() => onRemove(item._index)}
            />
          ))}
          <button onClick={onAddItem} style={addBtn}>+ Ajouter un item personnalisé</button>
        </div>
      )}
    </div>
  );
}

// --- Item ---

function ChecklistItem({ item, onToggle, onNote, onLabel, onRemove }) {
  const [showNote, setShowNote] = useState(!!item.note);

  return (
    <div style={itemStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          type="checkbox"
          checked={!!item.checked}
          onChange={onToggle}
          style={checkboxStyle}
        />
        {item.custom ? (
          <input
            type="text"
            value={item.label}
            onChange={(e) => onLabel(e.target.value)}
            placeholder="Mon item personnalisé…"
            style={{ ...customLabelStyle, textDecoration: item.checked ? "line-through" : "none", opacity: item.checked ? 0.6 : 1 }}
          />
        ) : (
          <span
            onClick={onToggle}
            style={{
              flex: 1,
              fontSize: 13,
              color: item.checked ? "#9ca3af" : "#f1f5f9",
              textDecoration: item.checked ? "line-through" : "none",
              cursor: "pointer",
              lineHeight: 1.4,
            }}
          >
            {item.label}
          </span>
        )}
        <button
          onClick={() => setShowNote((s) => !s)}
          style={{ ...smallBtnStyle, color: item.note ? "#a78bfa" : "#9ca3af" }}
          title={item.note ? "Voir/éditer la note" : "Ajouter une note"}
        >
          {item.note ? "📝" : "+"}
        </button>
        {item.custom && (
          <button onClick={onRemove} style={removeBtn} title="Supprimer cet item">✕</button>
        )}
      </div>
      {showNote && (
        <textarea
          value={item.note}
          onChange={(e) => onNote(e.target.value)}
          placeholder="Note pour cet item…"
          style={noteStyle}
          rows={2}
        />
      )}
    </div>
  );
}

// --- Styles ---

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 2500,
  display: "flex", alignItems: "center", justifyContent: "center", padding: 12,
};
const modal = {
  background: "#0f0f13", color: "#f1f5f9", borderRadius: 18,
  border: "1px solid rgba(255,255,255,0.12)",
  padding: 20, maxWidth: 640, width: "100%", maxHeight: "92vh", overflow: "auto",
};
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12 };
const closeBtn = {
  background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
  width: 30, height: 30, borderRadius: "50%", fontSize: 14, cursor: "pointer", flexShrink: 0,
};
const progressContainer = {};
const progressBg = {
  background: "rgba(255,255,255,0.08)", borderRadius: 999, height: 8, overflow: "hidden",
};
const progressFg = {
  background: "linear-gradient(90deg, #a78bfa, #60a5fa)", height: "100%", transition: "width 0.3s",
};
const sectionStyle = {
  marginBottom: 14, padding: 12, background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10,
};
const sectionHeaderBtn = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  width: "100%", background: "transparent", border: "none", color: "#fff",
  cursor: "pointer", padding: "4px 0", marginBottom: 4,
};
const itemStyle = {
  padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.05)",
};
const checkboxStyle = {
  width: 18, height: 18, accentColor: "#a78bfa", cursor: "pointer", flexShrink: 0,
};
const customLabelStyle = {
  flex: 1, background: "rgba(0,0,0,0.3)", border: "1px solid rgba(167,139,250,0.3)",
  borderRadius: 6, padding: "6px 9px", color: "#fff", fontSize: 13, outline: "none",
};
const smallBtnStyle = {
  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 6, padding: "2px 8px", fontSize: 12, cursor: "pointer", flexShrink: 0,
};
const removeBtn = {
  background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
  borderRadius: 6, color: "#f87171", padding: "2px 8px", fontSize: 12, cursor: "pointer", flexShrink: 0,
};
const noteStyle = {
  marginTop: 8, marginLeft: 28, width: "calc(100% - 28px)",
  background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 6, padding: "8px 10px", color: "#f1f5f9", fontSize: 12,
  outline: "none", fontFamily: "inherit", resize: "vertical",
};
const addBtn = {
  marginTop: 10, padding: "7px 12px", borderRadius: 7,
  border: "1px dashed rgba(167,139,250,0.4)", background: "rgba(167,139,250,0.05)",
  color: "#c4b5fd", cursor: "pointer", fontSize: 11, fontWeight: 600, width: "100%",
};
const footer = {
  display: "flex", justifyContent: "space-between", gap: 10,
  marginTop: 18, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)",
};
const btnDanger = {
  padding: "9px 14px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.3)",
  background: "rgba(239,68,68,0.08)", color: "#f87171",
  fontWeight: 600, cursor: "pointer", fontSize: 12,
};
const btnPrimary = {
  padding: "9px 16px", borderRadius: 8, border: "none",
  background: "#a78bfa", color: "#0f0f13", fontWeight: 700, cursor: "pointer", fontSize: 13,
};
